import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { FiPrinter } from "react-icons/fi";
import { APP_PATH } from "../../api/backendPath";
import { formatDateNumeric as tzDateNumeric } from "../../utils/datetime";
import { formatPercent, formatWholeAmount } from "../../utils/amountFormat";

/**
 * ImagingInvoiceModal — the single, shared invoice used everywhere in the
 * imaging app (Order Management action column + Appointment Detail print
 * button). Consolidating into one component guarantees the two entry points can
 * never drift in layout or in the fee math again.
 *
 * Pricing is RECOMPUTED here (not read from the stale backend snapshot):
 *   subtotal   = Σ test prices
 *   transportationFee= order.pricing.transportationFee (home-visit orders only)
 *   adminFee   = round((subtotal + transportationFee) * adminFeePercentage / 100)
 *   total      = subtotal + transportationFee + adminFee
 * so the admin fee always reflects the configured percentage (e.g. 5%).
 *
 * Props:
 *   open                 boolean — controls visibility
 *   onClose              () => void
 *   order                full order object (patient, tests, pricing, payment…)
 *   centerProfile        imaging profile for the header (optional; fetched if absent)
 *   adminFeePercentage   number (optional; fetched from fee config if absent)
 */
// Every money amount on the invoice is rendered through this helper so the "IQD" prefix is
// applied consistently and can never be forgotten on a new row. IQD has no minor unit, so
// amounts are rounded to a whole number (e.g. a 6% admin fee of 1,200.3 shows as 1,200).
// Mirrors the lab invoice.
const iqd = (v) => `IQD ${formatWholeAmount(v)}`;

const parsePrice = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return isNaN(n) ? 0 : n;
};

// Booking statuses reached only through the payment `complete` step (paid, or a COD order
// accepted). At these stages the booking's admin-fee snapshot is what the patient was charged.
const PAID_STAGE_STATUSES = [
  "ORDER_ACCEPTED",
  "TECHNICIAN_ASSIGNED",
  "TECHNICIAN_DISPATCHED",
  "IN_PROGRESS",
  "COMPLETED",
];

// ON_SITE / VISIT_CENTER → "On-Site", HOME_VISIT → "Home Visit" (matches the
// lab invoice), so the per-test Location cell never prints the raw code.
const formatLocation = (location) => {
  if (!location) return "N/A";
  const map = {
    HOME_VISIT: "Home Visit",
    VISIT_CENTER: "On-Site",
    ON_SITE: "On-Site",
    "VISIT CENTER": "On-Site",
    "HOME VISIT": "Home Visit",
    "ON-SITE": "On-Site",
  };
  return map[String(location).toUpperCase()] || location;
};

export default function ImagingInvoiceModal({
  open,
  onClose,
  order,
  centerProfile,
  adminFeePercentage,
}) {
  const { i18n } = useTranslation();
  const isInvoiceAr = i18n.language === "ar";
  const invoiceRef = useRef(null);

  // Admin-fee percentage: use the caller-provided value when present, otherwise
  // fetch the current fee configuration once so the component is self-contained.
  const [fetchedPct, setFetchedPct] = useState(null);
  useEffect(() => {
    if (!open || adminFeePercentage != null) return;
    let active = true;
    (async () => {
      try {
        const token = sessionStorage.getItem("usr_1a2b3c");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/fees/configuration`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const adminFeeCfg = res.data?.data?.admin_fee;
        if (
          active &&
          adminFeeCfg?.is_active &&
          adminFeeCfg?.percentage !== undefined
        ) {
          setFetchedPct(parseFloat(adminFeeCfg.percentage));
        }
      } catch (e) {
        /* fall back to default below */
      }
    })();
    return () => {
      active = false;
    };
  }, [open, adminFeePercentage]);

  // Admin fee percentage: caller-provided value, else the fee configuration fetched from the
  // admin panel. Falls back to 0 — NEVER a hardcoded rate: a missing/unreadable configuration
  // means no admin fee is configured, and inventing one would overcharge the patient (the old
  // 0.5% fallback was 10× off the configured 4% for imaging).
  const effectivePct =
    adminFeePercentage != null
      ? Number(adminFeePercentage)
      : fetchedPct != null
        ? fetchedPct
        : 0;

  // Center profile: use the prop when provided, otherwise fetch it once.
  const [fetchedProfile, setFetchedProfile] = useState(null);
  useEffect(() => {
    if (!open || centerProfile) return;
    let active = true;
    (async () => {
      try {
        const token = sessionStorage.getItem("usr_1a2b3c");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}${APP_PATH.imagingProfile}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (active && res.data?.data) setFetchedProfile(res.data.data);
      } catch (e) {
        /* header falls back to the letter avatar */
      }
    })();
    return () => {
      active = false;
    };
  }, [open, centerProfile]);

  const profile = centerProfile || fetchedProfile;

  const invoice = useMemo(() => {
    const tests = order?.tests || [];
    const testsTotal = tests.reduce((s, t) => s + parsePrice(t.price), 0);
    const loc = order?.preferredLocation || order?.preferredLocationRaw || "";
    const hasHomeVisit =
      loc === "Home Visit" ||
      loc === "HOME_VISIT" ||
      tests.some((t) => t.location === "HOME_VISIT");
    const transportationFee = hasHomeVisit
      ? parsePrice(order?.pricing?.transportationFee)
      : 0;
    // Once the patient has paid (or a COD order was accepted) the booking carries the admin
    // fee that was actually CHARGED (`pricing.adminFee`, snapshotted at payment). The invoice
    // is then a historical record and must print that figure, like the doctor invoice prints
    // the stored `admin_fee_amount`: the live percentage may have changed since, and with the
    // Total derived from the printed lines a re-derived fee would shift the Total away from
    // what was paid. Before payment the live percentage is the preview, as before.
    const isPaidStage = PAID_STAGE_STATUSES.includes(
      String(order?.status || "").toUpperCase(),
    );
    const snapshotAdminFee = order?.pricing?.adminFee;
    const useFeeSnapshot = isPaidStage && snapshotAdminFee != null;
    const pct = useFeeSnapshot
      ? Number(order?.pricing?.adminFeePercentage) || Number(effectivePct) || 0
      : Number(effectivePct) || 0;
    const adminFee = useFeeSnapshot
      ? Math.round(parsePrice(snapshotAdminFee))
      : Math.round((testsTotal + transportationFee) * (pct / 100));

    // Loyalty / promotion discount the patient redeemed. Persisted on the booking
    // (`imaging_bookings.loyalty_discount` / `.loyalty_points_used`) and surfaced on
    // `pricing` by the order-details endpoint, so it is authoritative for EVERY payment
    // method (incl. COD) and no longer depends on the payment transaction's metadata.
    const loyaltyDiscount = parsePrice(
      order?.pricing?.loyaltyDiscount ?? order?.loyaltyDiscount ?? 0,
    );
    const loyaltyPointsUsed = Number(
      order?.pricing?.loyaltyPointsUsed ?? order?.loyaltyPointsUsed ?? 0,
    );
    // The two other deductions that can sit between the bill and what the patient pays,
    // supplied by the gateway's order-details enrichment: the promo code discount and, for
    // an INSURANCE-method payment, the insurance deduction applied at checkout.
    const promoDiscount = parsePrice(order?.pricing?.promoDiscount || 0);
    const promoCode = order?.pricing?.promoCode || null;
    const insuranceCoverage = parsePrice(order?.pricing?.insuranceCoverage || 0);

    // Subtotal shown on the invoice = tests + transportation fee. That is EXACTLY the base the
    // admin-fee percentage is charged on (adminFee = (tests + delivery) × %), so a reader can
    // verify the admin-fee line themselves. Mirrors the doctor and lab invoices.
    const subtotal = testsTotal + transportationFee;

    // Total is DERIVED from the lines printed above it, exactly like the doctor invoice:
    // Subtotal + Admin Fee − Insurance Coverage − Loyalty Discount − Promo Discount.
    // It used to print the payment row's `amountPaid` instead, which is net of the promo and
    // of the insurance deduction — neither of which had a line — so a paid invoice with a
    // promo or insurance did not add up. Every deduction now has its own line, so the derived
    // Total equals the amount charged.
    const grossTotal = Math.round(subtotal + adminFee);
    const total = Math.max(
      0,
      grossTotal - insuranceCoverage - loyaltyDiscount - promoDiscount,
    );

    return {
      testsTotal,
      subtotal,
      transportationFee,
      adminFee,
      loyaltyDiscount,
      loyaltyPointsUsed,
      promoDiscount,
      promoCode,
      insuranceCoverage,
      total,
      hasHomeVisit,
      pct,
    };
  }, [order, effectivePct]);

  // The invoice is addressed to the PATIENT (the account holder) — on a child booking that is
  // the guardian — exactly like the doctor app's consultation invoice, which prints the patient
  // record's name and falls back to the child's name only when the account holder's name is
  // unavailable. Kept in step with it so every provider bills the same person.
  const invoicePatientName =
    order?.patient?.name ||
    [order?.child_name, order?.child_surname].filter(Boolean).join(" ") ||
    "N/A";

  const invL = isInvoiceAr
    ? {
        preview: "معاينة الفاتورة",
        print: "طباعة",
        close: "إغلاق",
        invoice: "فاتورة",
        tagline: "منصة الصحة الإلكترونية",
        patientDetails: "تفاصيل المريض",
        name: "الاسم",
        mobile: "الهاتف",
        ageGender: "العمر / الجنس",
        yr: "سنة",
        serviceDetails: "تفاصيل الخدمة",
        typeLocation: "النوع / الموقع",
        serviceDate: "تاريخ الخدمة",
        serviceTime: "وقت الخدمة",
        no: "#",
        test: "الفحص",
        location: "الموقع",
        // No "(د.ع)" here: every amount in the column now carries the IQD prefix itself.
        price: "السعر",
        subtotal: "المجموع الفرعي",
        adminFee: "رسوم إدارية",
        transportationFee: "رسوم النقل",
        free: "مجاناً",
        loyaltyDiscount: "خصم نقاط الولاء",
        pts: "نقطة",
        insuranceCoverage: "تغطية التأمين",
        promoDiscount: "خصم الرمز الترويجي",
        total: "الإجمالي",
        payment: "الدفع",
        method: "الطريقة",
        status: "الحالة",
        insurance: "التأمين",
        copayment: "نسبة المساهمة",
        deduction: "الخصم",
        noTests: "لا توجد فحوصات",
        notScheduled: "غير مجدول",
        homeVisit: "زيارة منزلية",
        imaging: "تصوير",
      }
    : {
        preview: "Invoice Preview",
        print: "Print",
        close: "Close",
        invoice: "INVOICE",
        tagline: "Electronic Health Platform",
        patientDetails: "Patient Details",
        name: "Name",
        mobile: "Mobile",
        ageGender: "Age / Gender",
        yr: "yr",
        serviceDetails: "Service Details",
        typeLocation: "Type / Location",
        serviceDate: "Service Date",
        serviceTime: "Service Time",
        no: "#",
        test: "Test",
        location: "Location",
        // No "(IQD)" here: every amount in the column now carries the IQD prefix itself.
        price: "Price",
        subtotal: "Subtotal",
        adminFee: "Admin Fee",
        transportationFee: "Transportation Fee",
        free: "Free",
        loyaltyDiscount: "Loyalty Point Discount",
        pts: "pts",
        insuranceCoverage: "Insurance Coverage",
        promoDiscount: "Promo Discount",
        total: "Total",
        payment: "Payment",
        method: "Method",
        status: "Status",
        insurance: "Insurance",
        copayment: "Copayment",
        deduction: "Deduction",
        noTests: "No tests",
        notScheduled: "Not scheduled",
        homeVisit: "Home Visit",
        imaging: "Imaging",
      };

  const centerName =
    profile?.imaging_center_name_english ||
    profile?.imaging_center_name_arabic ||
    "TIRYAQ";
  const centerAvatar =
    profile?.avatar_presigned_url || profile?.avatar_url || null;
  const centerInitial =
    (centerName || "T").trim().charAt(0).toUpperCase() || "T";

  const fmtStatus = (s) =>
    s
      ? String(s)
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "";

  // Format a stored time ("10:00:00" / "10:00") into a 12-hour label.
  const formatTime12h = (time) => {
    if (!time || typeof time !== "string") return invL.notScheduled;
    const [h, m] = time.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return invL.notScheduled;
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const period = h >= 12 ? "PM" : "AM";
    return `${displayHour}:${String(m).padStart(2, "0")} ${period}`;
  };

  const handlePrint = () => {
    const printContent = invoiceRef.current;
    if (!printContent) return;
    const isRtl = i18n.language === "ar";
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;
    printWindow.document
      .write(`<!DOCTYPE html><html dir="${isRtl ? "rtl" : "ltr"}" lang="${i18n.language}"><head><title>Invoice - ${order?.bookingNumber || order?.id || ""}</title>
      <style>
        * { box-sizing: border-box; font-family: Arial, Helvetica, sans-serif; }
        body { margin: 24px; color: #333; }
        h2 { margin: 0; }
        table { width: 100%; border-collapse: collapse; }
        @media print { body { margin: 0; padding: 16px; } }
      </style></head><body>${printContent.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (!open || !order) return null;

  const tests = order?.tests || [];
  const hasInsurance =
    order?.paymentMethod === "INSURANCE" ||
    (order?.insuranceStatus && order.insuranceStatus !== "N/A") ||
    order?.insuranceProviderId ||
    order?.insuranceDeductionAmount != null ||
    order?.insuranceCopaymentPercentage != null;

  const cellTh = (align) => ({
    background: "#f5f5f5",
    color: "#333",
    textAlign: align,
    padding: "8px 10px",
    fontSize: "12px",
    fontWeight: 600,
    borderBottom: "2px solid #ddd",
  });
  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    marginBottom: "4px",
  };
  const sectionTitle = {
    fontSize: "14px",
    fontWeight: 700,
    marginBottom: "8px",
    borderBottom: "1px solid #eee",
    paddingBottom: "4px",
  };
  const footCell = {
    padding: "8px 10px",
    fontSize: "13px",
    textAlign: isInvoiceAr ? "left" : "right",
  };
  const footLabel = {
    ...footCell,
    color: "#666",
  };

  return (
    <div
      onClick={onClose}
      dir={isInvoiceAr ? "rtl" : "ltr"}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 9999,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "30px 16px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "800px",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderBottom: "1px solid #e5e7eb",
            position: "sticky",
            top: 0,
            background: "#fff",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
            {invL.preview}
          </h4>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              onClick={handlePrint}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "#0cbdbd",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 20px",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              <FiPrinter size={16} /> {invL.print}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "#fff",
                color: "#333",
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "8px 20px",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              {invL.close}
            </button>
          </div>
        </div>

        {/* Printable content */}
        <div ref={invoiceRef} style={{ padding: "22px 24px" }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "2px solid #0cbdbd",
              paddingBottom: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              {centerAvatar ? (
                <img
                  src={centerAvatar}
                  alt=""
                  style={{
                    width: "54px",
                    height: "54px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "1px solid #e6fafa",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "54px",
                    height: "54px",
                    borderRadius: "50%",
                    background: "#e6fafa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    color: "#0cbdbd",
                    fontSize: "20px",
                  }}
                >
                  {centerInitial}
                </div>
              )}
              <div>
                <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700 }}>
                  {centerName}
                </h2>
                <p style={{ margin: "2px 0", fontSize: "12px", color: "#999" }}>
                  {invL.tagline}
                </p>
                {profile?.mobile_number && (
                  <p
                    style={{ margin: "2px 0", fontSize: "12px", color: "#999" }}
                  >
                    {profile.mobile_number}
                  </p>
                )}
              </div>
            </div>
            <div style={{ textAlign: isInvoiceAr ? "left" : "right" }}>
              <div
                style={{ fontSize: "24px", fontWeight: 700, color: "#0cbdbd" }}
              >
                {invL.invoice}
              </div>
              <div style={{ fontSize: "13px", color: "#666" }}>
                {order?.bookingNumber
                  ? `#${order.bookingNumber}`
                  : order?.id
                    ? `#${order.id}`
                    : ""}
              </div>
              <div style={{ fontSize: "12px", color: "#999" }}>
                {fmtStatus(order?.status) || ""}
              </div>
            </div>
          </div>

          {/* Patient details */}
          <div style={{ marginBottom: "18px" }}>
            <div style={sectionTitle}>{invL.patientDetails}</div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.name}</span>
              <span>{invoicePatientName}</span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.mobile}</span>
              <span>{order?.patient?.mobileNumber || "N/A"}</span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.ageGender}</span>
              <span>
                {order?.patient?.age != null
                  ? `${order.patient.age} ${invL.yr}`
                  : "N/A"}{" "}
                · {order?.patient?.gender || "N/A"}
              </span>
            </div>
          </div>

          {/* Payment */}
          <div style={{ marginTop: "18px", marginBottom: "8px" }}>
            <div style={sectionTitle}>{invL.payment}</div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.method}</span>
              <span style={{ fontWeight: 600, textTransform: "uppercase" }}>
                {order?.paymentMethod || "N/A"}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.status}</span>
              <span
                style={{
                  fontWeight: 600,
                  color:
                    order?.paymentStatus === "COMPLETED"
                      ? "#00BC7D"
                      : "#e09200",
                }}
              >
                {order?.paymentStatus || "N/A"}
              </span>
            </div>
          </div>

          {/* Service details */}
          <div style={{ marginBottom: "18px" }}>
            <div style={sectionTitle}>{invL.serviceDetails}</div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.typeLocation}</span>
              <span>
                {invL.imaging} ·{" "}
                {invoice.hasHomeVisit ? invL.homeVisit : "On-Site"}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.serviceDate}</span>
              <span>
                {order?.scheduledDate
                  ? tzDateNumeric(new Date(order.scheduledDate))
                  : invL.notScheduled}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.serviceTime}</span>
              <span>{formatTime12h(order?.scheduledTime)}</span>
            </div>
          </div>

          {/* Tests table */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={cellTh(isInvoiceAr ? "right" : "left")}>
                  {invL.no}
                </th>
                <th style={cellTh(isInvoiceAr ? "right" : "left")}>
                  {invL.test}
                </th>
                <th style={cellTh(isInvoiceAr ? "right" : "left")}>
                  {invL.location}
                </th>
                <th style={cellTh(isInvoiceAr ? "left" : "right")}>
                  {invL.price}
                </th>
              </tr>
            </thead>
            <tbody>
              {tests.map((tst, i) => (
                <tr key={i}>
                  <td
                    style={{
                      padding: "8px 10px",
                      fontSize: "13px",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {i + 1}
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      fontSize: "13px",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {tst.testName || tst.test_name || "N/A"}
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      fontSize: "13px",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {formatLocation(tst.location)}
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      fontSize: "13px",
                      borderBottom: "1px solid #eee",
                      textAlign: isInvoiceAr ? "left" : "right",
                    }}
                  >
                    {iqd(tst.price)}
                  </td>
                </tr>
              ))}
              {tests.length === 0 && (
                <tr>
                  <td
                    colSpan="4"
                    style={{ textAlign: "center", padding: "10px" }}
                  >
                    {invL.noTests}
                  </td>
                </tr>
              )}
            </tbody>
            {/* The tfoot rows read as a single top-to-bottom calculation, same order as the
                doctor and lab invoices:
                  tests                       (line items above)
                  + transportation fee              (home visit only)
                  = Subtotal                  (the base the admin fee % is charged on)
                  + admin fee
                  − insurance coverage        (INSURANCE-method payments only)
                  − loyalty point discount    (only when points were redeemed)
                  − promo discount            (only when a promo code was used)
                  = Total                     (what the patient actually paid) */}
            {tests.length > 0 && (
              <tfoot>
                {invoice.hasHomeVisit && (
                  <tr>
                    <td
                      colSpan="3"
                      style={{ ...footLabel, borderTop: "2px solid #333" }}
                    >
                      {invL.transportationFee}
                    </td>
                    <td style={{ ...footCell, borderTop: "2px solid #333" }}>
                      {Number(invoice.transportationFee) > 0
                        ? `+ ${iqd(invoice.transportationFee)}`
                        : invL.free}
                    </td>
                  </tr>
                )}
                <tr>
                  <td
                    colSpan="3"
                    style={{
                      ...footLabel,
                      fontWeight: 600,
                      borderTop: invoice.hasHomeVisit
                        ? "1px solid #eee"
                        : "2px solid #333",
                    }}
                  >
                    {invL.subtotal}
                  </td>
                  <td
                    style={{
                      ...footCell,
                      fontWeight: 600,
                      borderTop: invoice.hasHomeVisit
                        ? "1px solid #eee"
                        : "2px solid #333",
                    }}
                  >
                    {iqd(invoice.subtotal)}
                  </td>
                </tr>
                {Number(invoice.adminFee) > 0 && (
                  <tr>
                    <td colSpan="3" style={footLabel}>
                      {invL.adminFee}
                      {Number(invoice.pct)
                        ? ` (${formatPercent(invoice.pct)}%)`
                        : ""}
                    </td>
                    <td style={footCell}>+ {iqd(invoice.adminFee)}</td>
                  </tr>
                )}
                {/* Insurance deduction applied at checkout (INSURANCE-method payments
                    only) — same position as the doctor invoice's coverage line. */}
                {Number(invoice.insuranceCoverage) > 0 && (
                  <tr>
                    <td colSpan="3" style={footLabel}>
                      {invL.insuranceCoverage}
                    </td>
                    <td style={{ ...footCell, color: "#00BC7D" }}>
                      − {iqd(invoice.insuranceCoverage)}
                    </td>
                  </tr>
                )}
                {/* Loyalty points redeemed — shown as a deduction (green, like the doctor,
                    lab and pharmacy invoices) so the Total below reconciles with the amount
                    the patient was actually charged. */}
                {Number(invoice.loyaltyDiscount) > 0 && (
                  <tr>
                    <td colSpan="3" style={footLabel}>
                      {invL.loyaltyDiscount}
                      {invoice.loyaltyPointsUsed
                        ? ` (${invoice.loyaltyPointsUsed} ${invL.pts})`
                        : ""}
                    </td>
                    <td style={{ ...footCell, color: "#00BC7D" }}>
                      − {iqd(invoice.loyaltyDiscount)}
                    </td>
                  </tr>
                )}
                {/* Promo code discount — its own line so the derived Total still equals
                    what the patient pays. */}
                {Number(invoice.promoDiscount) > 0 && (
                  <tr>
                    <td colSpan="3" style={footLabel}>
                      {invL.promoDiscount}
                      {invoice.promoCode ? ` (${invoice.promoCode})` : ""}
                    </td>
                    <td style={{ ...footCell, color: "#00BC7D" }}>
                      − {iqd(invoice.promoDiscount)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td
                    colSpan="3"
                    style={{
                      padding: "10px",
                      fontWeight: 700,
                      borderTop: "1px solid #ccc",
                      textAlign: isInvoiceAr ? "left" : "right",
                    }}
                  >
                    {invL.total}
                  </td>
                  <td
                    style={{
                      padding: "10px",
                      fontWeight: 700,
                      borderTop: "1px solid #ccc",
                      textAlign: isInvoiceAr ? "left" : "right",
                      color: "#0cbdbd",
                    }}
                  >
                    {iqd(invoice.total)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>

          {/* Insurance box — only shown when the patient applied for insurance */}
          {hasInsurance && (
            <div
              style={{
                marginTop: "16px",
                padding: "14px 16px",
                border: "1px solid #d6f0f0",
                background: "#f2fbfb",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#0cbdbd",
                  marginBottom: "10px",
                }}
              >
                {invL.insurance}
              </div>
              <div style={{ ...rowStyle, marginBottom: "6px" }}>
                <span style={{ color: "#666" }}>{invL.status}</span>
                <span style={{ fontWeight: 600 }}>
                  {order?.insuranceStatus || "N/A"}
                </span>
              </div>
              <div style={{ ...rowStyle, marginBottom: "6px" }}>
                <span style={{ color: "#666" }}>{invL.copayment}</span>
                <span style={{ fontWeight: 600, color: "#e09200" }}>
                  {order?.insuranceCopaymentPercentage != null
                    ? `${formatPercent(order.insuranceCopaymentPercentage)}%`
                    : "N/A"}
                </span>
              </div>
              <div style={{ ...rowStyle, marginBottom: "6px" }}>
                <span style={{ color: "#666" }}>{invL.deduction}</span>
                <span style={{ fontWeight: 600 }}>
                  {order?.insuranceDeductionAmount != null
                    ? iqd(order.insuranceDeductionAmount)
                    : "N/A"}
                </span>
              </div>
            </div>
          )}

          {/* Footer — show the center name (not the platform name). */}
          <div
            style={{
              marginTop: "26px",
              textAlign: "center",
              fontSize: "12px",
              color: "#999",
              borderTop: "1px solid #eee",
              paddingTop: "12px",
            }}
          >
            {`${isInvoiceAr ? "شكراً لاختياركم" : "Thank you for choosing"} ${centerName}.`}
          </div>
        </div>
      </div>
    </div>
  );
}
